import { assertNever } from '@sparkdotfi/common-universal'

import { MigrationInfo } from '../../types'
import { formatPercentage } from '@/domain/common/format'
import { assets } from '@/ui/assets'
import { ExternalLinkButton } from '@/ui/atoms/link-button/LinkButton'
import { Panel } from '@/ui/atoms/panel/Panel'
import { links } from '@/ui/constants/links'
import { cn } from '@/ui/utils/style'

export function LegacyAccountBanner(props: MigrationInfo) {
  if (props.type === 'sdai-to-susds') {
    return <DaiLegacyAccountBanner {...props} />
  }

  if (props.type === 'susdc-to-spusdc') {
    return <UsdcLegacyAccountBanner />
  }

  assertNever(props)
}

function DaiLegacyAccountBanner({ apyImprovement }: Extract<MigrationInfo, { type: 'sdai-to-susds' }>) {
  return (
    <Panel spacing="xs" className="flex items-center gap-3 bg-primary sm:gap-8">
      <img
        src={assets.savings.sdaiUpgradeBannerIcon}
        alt="savings-upgrade-banner-icon"
        className="w-[72px] lg:w-[110px]"
      />
      <div
        className={cn(
          'grid w-full grid-rows-[1fr_auto] items-center',
          'gap-2 sm:gap-4 lg:grid-cols-[1fr_auto] lg:grid-rows-1 lg:gap-6',
        )}
      >
        <div className="flex flex-col gap-1">
          <div className="typography-label-2 sm:typography-heading-5 text-primary">
            This is a legacy Savings Account. <br className="hidden sm:inline" />
            You can deposit your DAI into USDS Savings <br className="hidden sm:inline" />
            {apyImprovement ? (
              <>
                and get <span className="text-system-success-primary">{formatPercentage(apyImprovement)}</span> higher
                APY!
              </>
            ) : (
              <>and unlock the full potential of Spark Savings.</>
            )}
          </div>
        </div>
        <ExternalLinkButton to={links.docs.savings.newSavings} variant="tertiary" className="sm:mr-4" size="m">
          Learn more
        </ExternalLinkButton>
      </div>
    </Panel>
  )
}

function UsdcLegacyAccountBanner() {
  return (
    <Panel spacing="xs" className="flex items-center gap-3 bg-primary sm:gap-8">
      <img
        src={assets.savings.susdcUpgradeBannerIcon}
        alt="savings-upgrade-banner-icon"
        className="w-[72px] lg:w-[110px]"
      />
      <div
        className={cn(
          'grid w-full grid-rows-[1fr_auto] items-center',
          'gap-2 sm:gap-4 lg:grid-cols-[1fr_auto] lg:grid-rows-1 lg:gap-6',
        )}
      >
        <div className="flex flex-col gap-1">
          <div className="typography-label-2 sm:typography-heading-5 text-primary">
            This is a legacy Savings Account. <br className="hidden sm:inline" />
            You can deposit your USDC into Spark Savings V2 <br className="hidden sm:inline" />
            and maximize your earnings with additional{' '}
            <span className="whitespace-nowrap bg-gradient-spark-primary-1 bg-clip-text text-transparent">
              Spark Points
            </span>
          </div>
        </div>
        <ExternalLinkButton to={links.docs.savings.earningSavings} variant="tertiary" className="sm:mr-4" size="m">
          Learn more
        </ExternalLinkButton>
      </div>
    </Panel>
  )
}
